import {TimeRegistrationTargetSuggestion} from '../../../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import Util from '../../../../../shared/util/util';
import {
	TimeReg,
	TimeRegEdge,
	TimeRegEntity,
	timeRegEntityInIdList,
	TimeRegistrationsForAWeek,
	timeRegMatchesEntityId,
} from './TimeRegUtil';
import {TIME_REG_TYPES, type TimeRegType} from '../../TimeRowUtils';

const getTimeRegType = (timeReg: TimeReg | TimeRegistrationTargetSuggestion | null): TimeRegType | undefined => {
	if (timeReg?.task && timeReg.task.project) {
		return TIME_REG_TYPES.TASK_TIME;
	} else if (timeReg?.project) {
		return TIME_REG_TYPES.PROJECT_TIME;
	} else if (timeReg?.idleTime) {
		return TIME_REG_TYPES.IDLE_TIME;
	} else {
		return undefined;
	}
};

export const getEntityFromSuggestion = (suggestion: TimeRegistrationTargetSuggestion): TimeRegEntity => {
	const rawEntity = suggestion?.task || suggestion?.idleTime || suggestion?.project;
	return {
		...rawEntity,
		disabled: false,
		type: getTimeRegType(suggestion),
		isSuggestion: true,
		minutesRegistered: undefined,
		billableMinutesRegistered: undefined,
		showAsSuggestion: true,
	} as TimeRegEntity; // FIXME: This typecast is a bit illegal. The object we get from the function isn't actually guaranteed to match the TimeRegEntity type. This should probably be refactored to be more type-safe.
};

export function getHasTimeRegistrationFromSuggestion(
	suggestionEntity: TimeRegEntity,
	timeRegistrations: TimeRegEdge[] | undefined
): boolean {
	if (!timeRegistrations) return false;
	return timeRegistrations.some(timeReg => timeRegMatchesEntityId(timeReg?.node, suggestionEntity.id));
}

function addTimeRegToSuggestionEntityInDayViewFormat(suggestionEntity: TimeRegEntity, timeReg: TimeReg) {
	suggestionEntity.id = timeReg.id;
	suggestionEntity.timeRegId = timeReg.id;
	suggestionEntity.type = getTimeRegType(timeReg);
	suggestionEntity.showAsSuggestion = true;
	suggestionEntity.notes = timeReg.notes;
	suggestionEntity.minutesRegistered = timeReg.minutesRegistered;
	suggestionEntity.billableMinutesRegistered = timeReg.billableMinutesRegistered;
}

/*
 * When the user has added a time reg to a suggestion in this setting, we want to add
 * the time reg to the suggestion while still showing it in the same place.
 * So given a list of all suggestion entities and a list of all time regs as well as a list of the suggestions that had no time reg when the page was loaded
 * We will update the suggestion Entities with time registrations
 * */
export function addTimeRegsToMovedSuggestions(
	suggestionEntities: Map<TimeRegEntity, TimeRegistrationsForAWeek>,
	timeRegistrations: TimeRegEdge[],
	suggestionsNotHavingTimeRegsAtLoad: string[],
	isDayView: boolean
): Map<TimeRegEntity, TimeRegistrationsForAWeek> {
	const suggestionEntitiesWithAddedTimeRegs = new Map([...suggestionEntities]);

	const timeRegistrationsAddedSincePageLoad = timeRegistrations.filter(timeReg => {
		return timeRegEntityInIdList(timeReg?.node, suggestionsNotHavingTimeRegsAtLoad);
	});
	timeRegistrationsAddedSincePageLoad.forEach(timeReg => {
		const suggestionEntity = [...suggestionEntities.keys()].find(suggestionEntity =>
			timeRegMatchesEntityId(timeReg?.node, suggestionEntity.id)
		);
		if (suggestionEntity) {
			const suggestionEntityTimeRegs = timeRegistrations.filter(timeRegEdge =>
				timeRegMatchesEntityId(timeRegEdge?.node, suggestionEntity.id)
			);

			const dayRegsForEntity: TimeRegistrationsForAWeek = [[], [], [], [], [], [], []];
			if (isDayView) {
				const timeReg = suggestionEntityTimeRegs[0]?.node;
				addTimeRegToSuggestionEntityInDayViewFormat(suggestionEntity, timeReg);
				suggestionEntitiesWithAddedTimeRegs.set(suggestionEntity, dayRegsForEntity);
			} else {
				suggestionEntityTimeRegs.forEach(timeRegEdge => {
					const timeReg = timeRegEdge?.node;
					const dayOfWeek = Util.CreateNonUtcMomentDate(timeReg.year, timeReg.month, timeReg.day).weekday();
					dayRegsForEntity[dayOfWeek].push(timeRegEdge);
				});
				suggestionEntitiesWithAddedTimeRegs.set(suggestionEntity, dayRegsForEntity);
			}
		}
	});
	return suggestionEntitiesWithAddedTimeRegs;
}

export function getTimeRegsWithoutMovedSuggestions(
	_filteredTimeRegs: TimeRegEdge[],
	suggestionIdsAtLoad: string[],
	timeRegsNotMatchedToSuggestions: string[]
): TimeRegEdge[] {
	let _filteredTimeRegsWithoutMovedSuggestions = _filteredTimeRegs;
	if (suggestionIdsAtLoad.length > 0) {
		_filteredTimeRegsWithoutMovedSuggestions = _filteredTimeRegs.filter(
			timeRegEdge =>
				!timeRegEntityInIdList(timeRegEdge?.node, suggestionIdsAtLoad) ||
				timeRegsNotMatchedToSuggestions.includes(timeRegEdge?.node.id)
		);
	}
	return _filteredTimeRegsWithoutMovedSuggestions;
}

/* We should make a week-view to day-view adapter, to eliminate the day view specific code.
 * Problem now is that the time_row_table is littered with day view specifics, so it cannot easily be done now.
 *
 * We should always have a simple object structure: a timeRegEntity that can hold a task, internal time etc. as well as a list of time regs
 * A bit like in week view, but including a full list of time registrations
 *
 * This list could then be displayed as individual time regs or as grouped time regs.
 *  */
type DayViewEntityOrTimeReg = {
	node: {
		billableMinutesRegistered: number | undefined;
		timeRegId: string;
		notes: string;
		task?: TimeRegEntity;
		project?: TimeRegEntity;
		idleTime?: TimeRegEntity;
		switchIdleTime?: boolean | undefined;
		switchProject?: boolean;
		name: string;
		id: string;
		minutesRegistered: number | undefined;
		type: string | undefined;
		showAsSuggestion?: boolean;
		isInternalTime?: boolean;
	};
};
export function formatForDayView(
	suggestionEntitiesWithTimeRegs: Map<TimeRegEntity, TimeRegistrationsForAWeek>
): DayViewEntityOrTimeReg[] {
	const returnValue: DayViewEntityOrTimeReg[] = [];
	[...suggestionEntitiesWithTimeRegs.keys()].forEach(suggestionEntity => {
		if (suggestionEntity.type === TIME_REG_TYPES.TASK_TIME) {
			returnValue.push({
				node: {
					id: suggestionEntity.id,
					timeRegId: suggestionEntity.timeRegId,
					name: suggestionEntity.name,
					notes: suggestionEntity.notes,
					minutesRegistered: suggestionEntity.minutesRegistered,
					billableMinutesRegistered: suggestionEntity.billableMinutesRegistered,
					task: suggestionEntity,
					type: suggestionEntity.timeRegId ? TIME_REG_TYPES.TASK_TIME : undefined,
					showAsSuggestion: suggestionEntity.showAsSuggestion,
				},
			});
		} else if (suggestionEntity.type === TIME_REG_TYPES.IDLE_TIME) {
			returnValue.push({
				node: {
					id: suggestionEntity.id,
					timeRegId: suggestionEntity.timeRegId,
					name: suggestionEntity.name,
					switchIdleTime: suggestionEntity.isSuggestion,
					idleTime: suggestionEntity,
					notes: suggestionEntity.notes,
					type: suggestionEntity.timeRegId ? TIME_REG_TYPES.IDLE_TIME : undefined,
					minutesRegistered: suggestionEntity.minutesRegistered,
					billableMinutesRegistered: suggestionEntity.billableMinutesRegistered,
					showAsSuggestion: suggestionEntity.showAsSuggestion,
					isInternalTime: suggestionEntity.isInternalTime,
				},
			});
		} else if (suggestionEntity.type === TIME_REG_TYPES.PROJECT_TIME) {
			returnValue.push({
				node: {
					id: suggestionEntity.id,
					timeRegId: suggestionEntity.timeRegId,
					name: suggestionEntity.name,
					switchProject: suggestionEntity.isSuggestion,
					notes: suggestionEntity.notes,
					minutesRegistered: suggestionEntity.minutesRegistered,
					billableMinutesRegistered: suggestionEntity.billableMinutesRegistered,
					project: suggestionEntity,
					type: suggestionEntity.timeRegId ? TIME_REG_TYPES.PROJECT_TIME : undefined,
					showAsSuggestion: suggestionEntity.showAsSuggestion,
				},
			});
		}
	});
	return returnValue;
}

/* We should make a week-view to day-view adapter, this stuff should not be necessary */
export function getTimeRegsNotMatchedToSuggestionsInDayView(
	suggestionEntities: Map<TimeRegEntity, TimeRegistrationsForAWeek>,
	timeRegistrations: TimeRegEdge[],
	suggestionsNotHavingTimeRegsAtLoad: string[]
): string[] {
	const timeRegsNotMatchedToSuggestions: string[] = [];
	const timeRegistrationsWithMovedSuggestions = timeRegistrations.filter(timeReg => {
		return timeRegEntityInIdList(timeReg?.node, suggestionsNotHavingTimeRegsAtLoad);
	});
	timeRegistrationsWithMovedSuggestions.forEach(timeReg => {
		const suggestionEntity = [...suggestionEntities.keys()].find(suggestionEntity =>
			timeRegMatchesEntityId(timeReg?.node, suggestionEntity.id)
		);
		if (suggestionEntity) {
			const suggestionEntityTimeRegs = timeRegistrations.filter(timeRegEdge =>
				timeRegMatchesEntityId(timeRegEdge?.node, suggestionEntity.id)
			);
			if (suggestionEntityTimeRegs.length > 1) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const [_firstElement, ...restOfArray] = suggestionEntityTimeRegs;
				timeRegsNotMatchedToSuggestions.push(...restOfArray.map(e => e?.node?.id));
			}
		}
	});
	return timeRegsNotMatchedToSuggestions;
}
